import { useEffect, useRef, useState } from 'react';
import { demoFixture, demoKind, type DemoItem, type DemoPoint } from '../../fixtures/demo-adapter';

export function RiskDemoMap({
  items,
  selected,
  select,
  historical,
  maximize,
  maximized,
}: {
  items: DemoItem[];
  selected: string;
  select: (id: string) => void;
  historical: boolean;
  maximize: () => void;
  maximized: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x: number; y: number; dx: number; dy: number; moved: boolean } | null>(
    null,
  );
  const previous = useRef(selected);
  const [size, setSize] = useState({ width: 1000, height: 600 });
  const [view, setView] = useState({ zoom: 1, x: 0, y: 0 });
  const [layers, setLayers] = useState({
    regions: true,
    sources: true,
    warning: true,
    forecast: true,
    satellite: true,
  });
  const [layerPanel, setLayerPanel] = useState(true);
  const width = Math.max(size.width, size.height * (2045 / 1420)),
    height = width / (2045 / 1420);
  const bound = (zoom: number, x: number, y: number) => ({
    zoom,
    x: Math.max(-(width * zoom - size.width) / 2, Math.min((width * zoom - size.width) / 2, x)),
    y: Math.max(-(height * zoom - size.height) / 2, Math.min((height * zoom - size.height) / 2, y)),
  });
  const clampX = Math.max(0, (width * view.zoom - size.width) / 2),
    clampY = Math.max(0, (height * view.zoom - size.height) / 2);
  const x = Math.max(-clampX, Math.min(clampX, view.x)),
    y = Math.max(-clampY, Math.min(clampY, view.y));
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new ResizeObserver(() =>
      setSize({ width: element.clientWidth, height: element.clientHeight }),
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const wheel = (event: WheelEvent) => {
      if ((event.target as HTMLElement).closest('button,input,label')) return;
      event.preventDefault();
      setView((current) => {
        const zoom = Math.min(3, Math.max(1, current.zoom * (event.deltaY < 0 ? 1.12 : 1 / 1.12)));
        const cx = Math.max(0, (width * zoom - size.width) / 2),
          cy = Math.max(0, (height * zoom - size.height) / 2);
        return {
          zoom,
          x: Math.max(-cx, Math.min(cx, current.x)),
          y: Math.max(-cy, Math.min(cy, current.y)),
        };
      });
    };
    element.addEventListener('wheel', wheel, { passive: false });
    return () => element.removeEventListener('wheel', wheel);
  }, [width, height, size.width, size.height]);
  useEffect(() => {
    if (previous.current === selected) return;
    previous.current = selected;
    const picked = items.find((item) => item.id === selected);
    if (!picked) return;
    const aspect = 2045 / 1420;
    const w = Math.max(size.width, size.height * aspect),
      h = w / aspect;
    setView((current) => {
      const cx = Math.max(0, (w * current.zoom - size.width) / 2),
        cy = Math.max(0, (h * current.zoom - size.height) / 2);
      return {
        zoom: current.zoom,
        x: Math.max(-cx, Math.min(cx, (0.5 - picked.point.x) * w * current.zoom)),
        y: Math.max(-cy, Math.min(cy, (0.5 - picked.point.y) * h * current.zoom)),
      };
    });
  }, [selected, items, size]);
  const zoom = (factor: number) =>
    setView((current) =>
      bound(Math.min(3, Math.max(1, current.zoom * factor)), current.x, current.y),
    );
  const position = (p: DemoPoint) => ({ left: `${p.x * 100}%`, top: `${p.y * 100}%` });
  return (
    <div
      ref={ref}
      className="risk-demo-map"
      data-r2-map
      data-r2-map-kind="illustrative"
      onPointerDown={(event) => {
        if ((event.target as HTMLElement).closest('button,input,label')) return;
        drag.current = { x: event.clientX, y: event.clientY, dx: x, dy: y, moved: false };
        event.currentTarget.setPointerCapture(event.pointerId);
      }}
      onPointerMove={(event) => {
        const d = drag.current;
        if (!d) return;
        if (Math.hypot(event.clientX - d.x, event.clientY - d.y) > 3) d.moved = true;
        setView((current) =>
          bound(current.zoom, d.dx + event.clientX - d.x, d.dy + event.clientY - d.y),
        );
      }}
      onPointerUp={() => {
        drag.current = null;
      }}
      onPointerCancel={() => {
        drag.current = null;
      }}
    >
      <div
        className="risk-map-content"
        data-r2-transform
        data-zoom={view.zoom}
        style={{
          width,
          height,
          left: (size.width - width) / 2,
          top: (size.height - height) / 2,
          transform: `translate(${x}px, ${y}px) scale(${view.zoom})`,
        }}
      >
        <img
          src="/normal-monitoring/demo/vs08-r2-map-background.png"
          alt="AI 生成地形示意背景，无真实地理配准"
          draggable={false}
        />
        {layers.regions && !historical && (
          <svg
            viewBox="0 0 1000 1000"
            preserveAspectRatio="none"
            className="risk-region-layer"
            aria-label="演示区域边界"
          >
            {demoFixture.monitoring.regions.map((region) => (
              <polygon
                key={region.id}
                data-r2-region={region.id}
                data-r2-polygon={region.id}
                data-selected={items.some(
                  (item) =>
                    item.id === selected && 'regionId' in item && item.regionId === region.id,
                )}
                points={region.polygon.map(([px, py]) => `${px * 1000},${py * 1000}`).join(' ')}
                className={`risk-region-${region.border}`}
              >
                <title>{`${region.name} · 示意区域，非真实辖区`}</title>
              </polygon>
            ))}
          </svg>
        )}
        {items
          .filter((item) =>
            item.kind === 'SATELLITE_SAMPLE'
              ? layers.satellite
              : item.kind === 'OBSERVATION_WARNING'
                ? layers.warning
                : layers.forecast,
          )
          .map((item) => (
            <button
              type="button"
              key={item.id}
              className={`risk-map-marker risk-${item.kind === 'SATELLITE_SAMPLE' ? 'satellite' : demoKind(item)}`}
              style={position(item.point)}
              data-r2-marker
              data-id={item.id}
              data-x={item.point.x}
              data-y={item.point.y}
              aria-label={`${item.title} · ${item.time} · 演示示例`}
              aria-pressed={selected === item.id}
              title={`${item.title} · ${item.time} · 非实时演示`}
              onClick={() => select(item.id)}
            >
              <i />
              <span>
                {item.kind === 'SATELLITE_SAMPLE'
                  ? `热异常 ${item.id.slice(-3)}`
                  : item.title.replace(' · ', '\n')}
              </span>
            </button>
          ))}
        {layers.sources &&
          !historical &&
          demoFixture.monitoring.sources
            .filter((source) => source.point)
            .map((source) => (
              <button
                type="button"
                className="risk-map-marker risk-source"
                style={position(source.point!)}
                key={source.id}
                data-r2-source={source.id}
                title={`${source.name} · ${source.state} · DEMO`}
                aria-label={`${source.name} · 演示来源详情`}
                onClick={() => select(source.id)}
                aria-pressed={selected === source.id}
              >
                <i />
                <span>示例来源点</span>
              </button>
            ))}
      </div>
      <section className="risk-map-layers" aria-label="示意地图图层">
        <div>
          <span className="risk-map-tab">▧ 示意底图</span>
          <button
            type="button"
            aria-expanded={layerPanel}
            onClick={() => setLayerPanel(!layerPanel)}
          >
            图层 {layerPanel ? '⌃' : '⌄'}
          </button>
        </div>
        {layerPanel && (
          <div className="risk-layer-options">
            {(
              [
                ...(historical
                  ? [['satellite', '卫星热异常']]
                  : [
                      ['regions', '显示区域边界'],
                      ['sources', '显示来源点'],
                      ['warning', '观测预警'],
                      ['forecast', '预测关注'],
                    ]),
              ] as [keyof typeof layers, string][]
            ).map(([key, label]) => (
              <label key={key}>
                <input
                  type="checkbox"
                  data-r2-layer={key}
                  checked={layers[key]}
                  onChange={(event) =>
                    setLayers((current) => ({ ...current, [key]: event.target.checked }))
                  }
                />
                {label}
              </label>
            ))}
          </div>
        )}
      </section>
      <div className="risk-map-tools">
        <button
          type="button"
          aria-label="重置视图"
          data-r2-reset
          onClick={() => setView({ zoom: 1, x: 0, y: 0 })}
        >
          ⊙
        </button>
        <button type="button" aria-label="放大地图" data-r2-zoom="in" onClick={() => zoom(1.25)}>
          ＋
        </button>
        <button type="button" aria-label="缩小地图" data-r2-zoom="out" onClick={() => zoom(0.8)}>
          −
        </button>
        <button
          type="button"
          aria-label={maximized ? '还原地图' : '地图最大化'}
          data-r2-maximize
          aria-pressed={maximized}
          onClick={maximize}
        >
          {maximized ? '↙' : '⛶'}
        </button>
      </div>
      <div className="risk-map-legend">
        <strong>图例 · 演示</strong>
        {historical ? (
          <span>
            <i className="risk-satellite" />
            卫星历史热异常 · 未核实
          </span>
        ) : (
          <>
            <span>
              <i className="risk-warning" />
              观测预警
            </span>
            <span>
              <i className="risk-forecast" />
              独立预测关注
            </span>
            <span>
              <i className="risk-source" />
              示例来源点
            </span>
          </>
        )}
        <span>▱ 图像相对位置 · 无地理配准</span>
      </div>
      <div className="risk-map-attribution">AI 示意地形 · 非测绘地图 · 无地理比例尺</div>
    </div>
  );
}
